import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { PLATFORM_LABELS, istLocal, useSmmConnections } from "@/lib/smm";

const inputCls = "w-full rounded-lg border border-slate-200 p-2.5 text-sm focus:border-emerald-500 outline-none";

// Schedule an approved item on one or more platforms (time entered in IST).
export const ScheduleDialog = ({ item, onClose, onDone }) => {
  const [when, setWhen] = useState("");
  const [platforms, setPlatforms] = useState([]);
  const conn = useSmmConnections();
  const auto = platforms.filter((p) => conn[p]);
  const manual = platforms.filter((p) => !conn[p]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (item) {
      setWhen(istLocal(new Date(Date.now() + 60 * 60 * 1000).toISOString()));
      setPlatforms(item.platforms);
    }
  }, [item]);
  if (!item) return null;
  const toggle = (p) => setPlatforms(platforms.includes(p) ? platforms.filter((x) => x !== p) : [...platforms, p]);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/smm/posts", { item_id: item.id, platforms, scheduled_at: when });
      toast.success(`${data.created.length} post(s) added to the calendar`);
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="smm-schedule-dialog">
        <DialogHeader><DialogTitle>Schedule post</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-600 line-clamp-2">{item.title}</p>
        <label className="block space-y-1">
          <span className="text-xs font-semibold text-slate-600">Date & time (India Standard Time)</span>
          <input type="datetime-local" className={inputCls} value={when} onChange={(e) => setWhen(e.target.value)} data-testid="smm-schedule-when" />
        </label>
        <div className="space-y-1">
          <span className="text-xs font-semibold text-slate-600">Platforms</span>
          <div className="flex flex-wrap gap-1.5">
            {Object.keys(PLATFORM_LABELS).map((p) => (
              <button key={p} onClick={() => toggle(p)} data-testid={`smm-schedule-platform-${p}`}
                className={`text-xs font-semibold px-3 py-1.5 rounded-full border ${platforms.includes(p) ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-500 border-slate-200"}`}>{PLATFORM_LABELS[p]}</button>
            ))}
          </div>
        </div>
        <p className="text-[11px] text-slate-500" data-testid="smm-schedule-mode-note">
          At this time (IST) posts go out on their own to connected accounts{auto.length ? `: ${auto.map((p) => PLATFORM_LABELS[p]).join(", ")}` : " (none connected yet)"}.
          {manual.length > 0 && ` ${manual.map((p) => PLATFORM_LABELS[p]).join(", ")} can't be posted automatically - you'll get a "Due - post now" reminder to post by hand.`}
        </p>
        <button onClick={submit} disabled={busy || !platforms.length || !when} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-2.5 rounded-lg disabled:opacity-60" data-testid="smm-schedule-submit">
          {busy ? "Scheduling…" : `Schedule ${platforms.length} post(s)`}
        </button>
      </DialogContent>
    </Dialog>
  );
};
